import Button from "components/shared/button";
import { useAppDispatch, useAppSelector } from "store/hooks";
import CompareFields, { COMPARE_FIELD_TYPES, type CompareField } from "./CompareFields";
import useTranslation, { _T } from "utils/hooks/useTranslation";
import Checkbox from "components/shared/inputs/checkbox";
import { useSalesNetwork } from "utils/hooks/useSalesNetworks";
import { useCallback, useEffect, useState } from "react";
import * as ProjectStore from "store/projectSlice";
import { getAppUrl } from "utils/defines";
import { useNavigate } from "react-router";
import { StickyElementOffset } from "components/shared/stickystuck";
import numeral from "numeral";
import { selectProduct as selectProductAction } from "store/projectSlice";
import { useProject } from "utils/hooks/useProject";

const StepCompare: React.FC = () => {
    const projectStore = useAppSelector((state) => state.project);
    const { t } = useTranslation();
    const { network } = useSalesNetwork();
    const [showEqualRows, setShowEqualRows] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    // const [isStickyButton, setIsStickyButton] = useState(false);
    const [isStickyDesktop, setIsStickyDesktop] = useState(false);
    const [isStickyMobile, setIsStickyMobile] = useState(false);
    const count = projectStore.project.selectedCompareProducts?.length;
    const { isHybride } = useProject();
    const currency = network?._parameters.currency;

    useEffect(() => {
        if (!count)
            navigate(getAppUrl('/project/results'));
    }, []);

    const getValue = (product: any, field: CompareField) => {
        const value = field.route
            ? product[field.route]?.[field.fieldName]
            : product[field.fieldName];

        if (!field.type) return value ?? "—";

        switch (field.type) {
            case COMPARE_FIELD_TYPES.SUM: {
                const items = field.route
                    ? product[field.route]
                    : product[field.fieldName];

                const sum = items?.reduce(
                    (sum: number, row: any) => sum + (Number(row?.[field.fieldName]) || 0),
                    0
                )

                return sum ? sum : field.unit == "currency" ? "—" : 0;
            }
            case COMPARE_FIELD_TYPES.HTML:
                return value != null ? <div dangerouslySetInnerHTML={{ __html: value }} /> : "—";

            case COMPARE_FIELD_TYPES.NUMERAL:
                return value != null ? numeral(value).format() : "—";

            default:
                return value ?? "—";

        }
    };

    // const renderValue = (value: any, field: CompareField) => {
    //     switch (field.type) {
    //         case COMPARE_FIELD_TYPES.HTML:
    //             return <div dangerouslySetInnerHTML={{ __html: value }} />;

    //         case COMPARE_FIELD_TYPES.NUMERAL:
    //             return value != null ? numeral(value).format() : "—";

    //         default:
    //             return value ?? "—";
    //     }
    // };

    // const renderUnit = (unit?: string | null) => {
    //     return unit ? unit == "currency" ? ` ${network?._parameters.currency}` : ` ${unit}` : "";
    // };

    const emptyCompareItems = () => {
        dispatch(ProjectStore.setSelectedCompareProducts([]));
        dispatch(ProjectStore.setSelectedCompareProductIds([]));

        navigate(getAppUrl('/project/results'));
    }


    const selectProduct = useCallback((e: any, item: any) => {
        if (e) e.stopPropagation();
        dispatch(selectProductAction(item));
        navigate(getAppUrl('/project/results'));
    }, [])

    const isAeroAndTypoIndiff = useCallback(() => {
        return projectStore.project.forms.hpChoiceStep.form.heatPumpType === '1' && projectStore.project.forms.hpChoiceStep.form.typologie === '0'
    }, [projectStore])

    const CompareHeaderRow = ({ title, products }: any) => (
        <group data-wrap="no">
            <group
                // data-length="autofit"
                data-align="center"
                data-gap="10"
                data-space-left="10"
                data-border-bottom="inset"
                data-length="360"
            >
                <text data-weight="500" data-text-size="22">{title}</text>
            </group>

            {products?.map((_: any, i: number) => (
                <group
                    key={i}
                    data-border-bottom="inset"
                    data-length="360"
                    data-height="50"
                    // data-space-horizontal="10"
                    data-align="center"
                // data-min-length="200"
                />
            ))}
        </group>
    );

    const normalize = (v: any) =>
        typeof v === 'string' ? v.trim() : v;

    const CompareDataRow = ({ label, field, products }: any) => {
        const allSame = products?.every((p: any, _i: number, arr: any[]) => {
            if (field.type === COMPARE_FIELD_TYPES.HTML) {
                const pValue = p.product[field.fieldName];
                const arrValue = arr[0].product[field.fieldName];

                return normalize(
                    pValue
                ) === normalize(
                    arrValue
                );
            }

            return normalize(getValue(p, field)) === normalize(getValue(arr[0], field));
        });

        return <group data-direction="column">
            <group data-wrap="no">
                <group
                    data-length="360"
                    data-min-height="50"
                    data-border-bottom="inset"
                    data-space-horizontal="20"
                    data-align="center"
                    data-direction="row"
                    data-wrap="no"
                >
                    <text
                        data-position="left"
                        data-weight="500"
                        data-text-size="14"
                        data-color="grey-color-mid"
                        data-wrap="wrap"
                    >
                        {label == "COMP_THERMO_POWER" && isHybride() ? t("COMP_PCALO_A0W50") : t(label)}
                    </text>
                </group>

                <group
                    {...(showEqualRows && !allSame ? {
                        "data-background": "custom-highlight"
                    } : {})}
                    data-radius="5"
                    data-wrap="no"
                    data-min-height="50"
                    data-direction="row"
                    data-length={products?.length * 360}
                    data-sticky-row
                // data-length="forcefit"
                >
                    {products?.map((p: any, i: number) => {
                        const val = getValue(p, field);
                        return (
                            <group
                                key={i}
                                // data-length="autofit"
                                data-align="center"
                                data-direction="row"
                                // data-shrink="no"
                                // data-min-length="200"
                                data-length="360"
                                data-border-bottom="inset"
                            >

                                <text data-position="center" data-color="grey-color-dark" data-weight="400"  >
                                    {val}
                                    {/* {renderUnit(field.unit)} */}
                                </text>
                            </group>
                        );
                    })}

                </group>
            </group>
        </group >
    };


    return <>
        <group>

            <StickyElementOffset
                offestTop={640}
                setIsSticky={setIsStickyDesktop}
                data-sticky="top"
                data-sticky-level={network?.code == "pac-air-eau" ? "60" : "90"}
                data-index="3"
                data-adaptive="desktop"
            >
                <group data-direction="column" data-align="center"
                >
                    {isStickyDesktop && <group
                        data-background="custom-highlight"
                        data-height="80"
                        data-align="center"
                        data-justify="center"
                        data-wrap="no"
                        data-length="forcefit"
                    >

                        <group data-space="10" data-length="360" data-text-size="large" data-weight="600" data-align="start" data-justify="start" >
                            {count > 1 && <Checkbox
                                name="3"
                                simple
                                label={t("DISPLAY_DIFFERENCES")}
                                checked={showEqualRows}
                                onChange={() => setShowEqualRows(prev => !prev)}
                            />}
                        </group>
                        {projectStore.project.selectedCompareProducts?.map((p: any) =>
                            <group data-space-vertical="10" data-length="360" data-background="custom-highlight">
                                <group data-align="center" data-justify="center" data-weight="600"><text data-text-size="large">{p.Product}</text></group>
                                <group data-align="center" data-justify="center" data-color="main"><text data-text-size="large">{p.product.price} {currency} {_T('HT')}</text></group>
                            </group>
                        )}
                    </group>}
                </group>
            </StickyElementOffset>

            <StickyElementOffset
                offestTop={640}
                setIsSticky={setIsStickyMobile}
                data-sticky="top"
                data-sticky-level={"90"}
                data-index="3"
                data-adaptive="mobile"
            >
                <group data-direction="column" data-align="center" data-length={projectStore.project.selectedCompareProducts?.length * 360}
                >
                    {isStickyMobile && <group
                        data-background="main-color-custom-lighter"
                        data-height="80"
                        data-align="center"
                        data-wrap="no"
                        data-overflow="auto"
                        data-sticky-row
                    >

                        <group data-space="10" data-length="360" data-text-size="large" data-weight="600" data-align="start" data-justify="start" >
                            {count > 1 && <Checkbox
                                name="3"
                                simple
                                label={t("DISPLAY_DIFFERENCES")}
                                checked={showEqualRows}
                                onChange={() => setShowEqualRows(prev => !prev)}
                            />}
                        </group>
                        {projectStore.project.selectedCompareProducts?.map((p: any) =>
                            <group data-space="10" data-length="360" >
                                <group data-align="center" data-justify="center" data-weight="600" data-length="360"><text data-text-size="large">{p.Product}</text></group>
                                <group data-align="center" data-justify="center" data-color="main" data-length="360" ><text data-text-size="large">{p.product.price} {currency} {_T('HT')}</text></group>
                            </group>
                        )}
                    </group>}
                </group>
            </StickyElementOffset>
            <group data-align="center" data-justify="space-around" >
                <group data-gap="30" data-wrap="no" data-direction="column" data-width="auto" >
                    <group>
                        <a
                            href="#"
                            data-type="group"
                            data-width="auto"
                            data-gap="5"
                            data-space-vertical="10"
                            data-interactive="highlight"
                            onClick={() => navigate(getAppUrl('/project/results'))}
                        >
                            <icon data-color="secondary" data-svg-size="20">
                                chevron_left
                            </icon>
                            <text data-text-size="medium" data-ellipsis="" data-color="secondary">
                                {t("BACK_RESULTS")}
                            </text>
                        </a>

                    </group>
                    <group data-wrap="no" >
                        <group data-length="360" data-background="main-background" data-gap="10" data-direction="column" data-space-left="10">
                            <group data-text-size="36" data-color="main" data-weight="600"><text>{t("COMPARATOR")}</text></group>
                            <group data-text-size="22" data-weight="600"><text>{count == 1 ? t("COMP_MODEL") : t("COMP_MODELS", { X: count })}</text></group>
                            {count > 1 && <group data-space-top="10" data-text-size="large" data-weight="600">
                                <Checkbox
                                    name="3"
                                    simple
                                    label={t("DISPLAY_DIFFERENCES")}
                                    checked={showEqualRows}
                                    onChange={() => setShowEqualRows(prev => !prev)}
                                />
                            </group>}


                            <group data-space-top="10" data-align="center">
                                <a
                                    href="#"
                                    data-type="group"
                                    data-width="auto"
                                    data-gap="5"
                                    data-interactive="highlight"
                                    onClick={emptyCompareItems}
                                >
                                    <icon data-color="secondary" data-svg-size="20">
                                        delete
                                    </icon>
                                    <text data-text-size="medium" data-ellipsis="" data-color="secondary">
                                        {t("EMPTY_COMPARATOR")}
                                    </text>
                                </a>
                            </group>
                        </group>

                        {projectStore.project.selectedCompareProducts?.map((p: any) =>
                            <div data-length="360">
                                {/* <div data-length="autofit" >  */}
                                <group data-direction="column" data-align="center" data-gap="10" data-space="20" data-space-top="0">
                                    <group data-space-vertical="20" data-justify="center">
                                        <group data-shrink="no" data-height="170" >
                                            <picture data-object-fit="contain" data-radius="atlantic-10">
                                                <img src={p.product.photo} alt="" data-radius="special" />
                                            </picture>
                                        </group>
                                    </group>
                                    <group data-align="center" data-justify="center" data-weight="600"><text data-text-size="22">{p.Product}</text></group>
                                    <group data-align="center" data-justify="center"><text data-text-size="18">
                                        {isAeroAndTypoIndiff() && <text data-text-size="small" data-color="grey-color-dark" data-wrap="wrap">
                                            {t(`SELECT_TYPO_${p.Split_mono?.toUpperCase()}`)}
                                        </text>}
                                    </text></group>
                                    <group data-align="center" data-justify="center" data-color="grey-color-mid"><text data-text-size="14">{t("CODE")}: {p.product.code_env}</text></group>
                                    <group data-align="center" data-justify="center" data-color="main"><text data-text-size="30">{p.product.price} {currency} {_T('HT')}</text></group>
                                    <Button
                                        secondary large
                                        data-height="40"
                                        toggleClassName="open"
                                        onClick={(e) => selectProduct(e, p)}
                                    >
                                        <text>{t("SELECT")}</text>
                                    </Button>
                                </group>
                            </div>
                        )}
                    </group>
                    <group data-width="auto" data-wrap="no" data-direction="column">
                        {Object.entries(CompareFields).map(([key, field]) =>
                            !field ? (
                                <CompareHeaderRow
                                    key={key}
                                    title={t(key)}
                                    products={projectStore.project.selectedCompareProducts}
                                />
                            ) : (
                                <CompareDataRow
                                    key={key}
                                    label={key}
                                    field={field}
                                    products={projectStore.project.selectedCompareProducts}
                                />
                            )
                        )}
                    </group>
                    <group data-direction="row" data-gap="10" data-justify="center" data-sticky-row>
                        <group data-length="360"></group>
                        {projectStore.project.selectedCompareProducts?.map((p: any) =>
                            <group
                                data-length="360"
                                data-justify="center"
                                data-space-bottom="10"
                            >
                                <Button
                                    secondary large
                                    data-height="40"
                                    toggleClassName="open"
                                    onClick={(e) => selectProduct(e, p)}
                                >
                                    <text>{t("SELECT")}</text>
                                </Button>
                            </group>
                        )}
                    </group>
                </group>
            </group>


        </group>


    </>
}

export default StepCompare;